import API_BASE from '../config';
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Mail } from 'lucide-react';

const API_URL = API_BASE;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [orderInfo, setOrderInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0); // used to trigger re-render during polling

  const provider = searchParams.get('provider');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    if (provider === 'razorpay' && orderId) {
      checkRazorpayOrder();
    } else {
      setStatus('failed');
    }
  }, []);

  const checkRazorpayOrder = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await fetch(`${API_URL}/api/payments/razorpay/order-status/${encodeURIComponent(orderId)}`);
        const data = await response.json();

        if (data.payment_status === 'paid') {
          setStatus('success');
          setOrderInfo(data);
          // Intentionally NO auto-redirect. Customer completes the intake form
          // at their own pace via the emailed link (or the fallback link below).
          return;
        }

        if (data.status === 'pending') {
          // Payment not confirmed yet — wait and retry
          await new Promise(res => setTimeout(res, pollInterval));
          setPollCount(attempt + 1);
          continue;
        }

        setStatus('failed');
        return;
      } catch (error) {
        console.error('Error checking Razorpay order status:', error);
        await new Promise(res => setTimeout(res, pollInterval));
      }
    }

    setStatus('failed');
  };

  const handleContinue = () => {
    if (orderInfo?.intake_url) {
      navigate(orderInfo.intake_url, { replace: true });
    }
  };

  return (
    <div className="payment-result-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .payment-result-page, .payment-result-page * {
          font-family: 'Poppins', sans-serif;
        }

        .payment-result-page {
          --blue: #7c3aed;
          --blue-light: #f6f4fd;
          --blue-mid: #d8cdf5;
          --blue-dark: #6d28d9;
          --navy: #1a0c38;
          --success: #16a34a;
          --success-bg: #dcfce7;
          --danger: #dc2626;
          --danger-bg: #fee2e2;
          --slate-50: #f8fafc;
          --slate-100: #f1f5f9;
          --slate-200: #e2e8f0;
          --slate-400: #94a3b8;
          --slate-500: #64748b;
          --slate-600: #475569;
          --slate-700: #334155;
          --slate-900: #0f172a;
        }

        .payment-result-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f7fc 0%, #f6f4fd 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }
        
        .logo {
          position: absolute;
          top: 24px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .logo img {
          height: 22px;
          width: auto;
          display: block;
        }
        
        .card {
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(26, 111, 214, 0.15);
          padding: 48px 40px;
          max-width: 480px;
          width: 100%;
          text-align: center;
        }
        
        .icon-circle {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
        }
        .icon-circle.checking {
          background: var(--blue-light);
        }
        .icon-circle.success {
          background: var(--success-bg);
        }
        .icon-circle.failed {
          background: var(--danger-bg);
        }
        .icon-circle svg {
          width: 40px;
          height: 40px;
        }
        .icon-circle.checking svg {
          color: var(--blue);
          animation: spin 1s linear infinite;
        }
        .icon-circle.success svg {
          color: var(--success);
        }
        .icon-circle.failed svg {
          color: var(--danger);
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .title {
          font-size: 24px;
          font-weight: 700;
          color: var(--slate-900);
          margin-bottom: 8px;
        }
        .subtitle {
          font-size: 15px;
          color: var(--slate-500);
          line-height: 1.6;
          margin-bottom: 24px;
        }
        
        .order-box {
          background: var(--blue-light);
          border: 2px solid var(--blue);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
        }
        .order-label {
          font-size: 11px;
          font-weight: 600;
          color: var(--slate-500);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }
        .order-id {
          font-size: 28px;
          font-weight: 800;
          color: var(--blue);
          letter-spacing: 1px;
          font-family: 'SF Mono', 'Fira Code', monospace;
        }
        
        .continue-btn {
          width: 100%;
          padding: 14px 24px;
          background: var(--blue);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.15s;
        }
        .continue-btn:hover {
          background: #155bb5;
        }
        
        .auto-redirect {
          font-size: 13px;
          color: var(--slate-500);
          margin-top: 16px;
        }

        .email-notice {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          text-align: left;
          background: var(--blue-light);
          border: 1px solid var(--blue-mid);
          border-radius: 12px;
          padding: 16px 18px;
          margin-bottom: 20px;
        }
        .email-notice svg {
          color: var(--blue);
          flex-shrink: 0;
          margin-top: 2px;
        }
        .email-notice-text {
          font-size: 14px;
          color: var(--slate-700);
          line-height: 1.55;
        }
        .email-notice-text strong {
          color: var(--slate-900);
          word-break: break-all;
        }

        .fallback-tip {
          font-size: 13px;
          color: var(--slate-500);
          line-height: 1.6;
          margin-top: 4px;
        }
        .fallback-link {
          background: none;
          border: none;
          padding: 0;
          color: var(--blue);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          text-decoration: underline;
        }
        .fallback-link:hover {
          color: var(--blue-dark);
        }

        .home-btn {
          margin-top: 20px;
          width: 100%;
          padding: 11px 20px;
          border-radius: 10px;
          border: 1px solid var(--slate-200);
          background: white;
          color: var(--slate-700);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }
        .home-btn:hover {
          background: var(--slate-50);
          border-color: var(--slate-300);
        }
        
        .retry-btn {
          padding: 12px 24px;
          background: white;
          color: var(--slate-700);
          border: 1px solid var(--slate-300);
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }
        .retry-btn:hover {
          background: var(--slate-50);
        }
        
        @media (max-width: 520px) {
          .card {
            padding: 36px 24px;
          }
          .order-id {
            font-size: 24px;
          }
        }
      `}</style>

      <div className="logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
        <img src="/images/brand/successlane-logo-mark.png" alt="SuccessLane" />
      </div>

      <div className="card">
        {/* Checking Status */}
        {status === 'checking' && (
          <>
            <div className="icon-circle checking">
              <Loader2 />
            </div>
            <h1 className="title">Confirming Payment...</h1>
            <p className="subtitle">
              Please wait while we verify your payment with Razorpay. This usually takes just a few seconds.
            </p>
            {orderId && (
              <div className="order-box">
                <div className="order-label">Your Order ID</div>
                <div className="order-id">{orderId}</div>
              </div>
            )}
          </>
        )}

        {/* Success */}
        {status === 'success' && (
          <>
            {/* Order ID — top of card */}
            {orderInfo?.order_id && (
              <div className="order-box" data-testid="payment-order-box" style={{ marginTop: 0, marginBottom: 28 }}>
                <div className="order-label">Your Order ID</div>
                <div className="order-id" data-testid="payment-order-id">{orderInfo.order_id}</div>
              </div>
            )}

            <div className="icon-circle success">
              <CheckCircle />
            </div>
            <h1 className="title" data-testid="payment-success-heading">
              {orderInfo?.customer_name
                ? `You're in, ${orderInfo.customer_name.split(' ')[0]}! 🚀`
                : "You're in! 🚀"}
            </h1>
            <p className="subtitle">
              Payment received — and our recruiter-certified experts are already rolling up their sleeves
              to craft a LinkedIn profile that gets you noticed for the right roles.
            </p>

            <div className="email-notice" data-testid="intake-email-notice">
              <Mail size={20} />
              <div className="email-notice-text">
                We've just sent your intake form to
                {orderInfo?.customer_email ? (
                  <> <strong>{orderInfo.customer_email}</strong></>
                ) : ' your email'}.
                Your 48-hour rewrite clock starts the moment you submit it — fill it out today
                so our recruiters can get started and get you back on page 1 sooner. The link stays live 🎯
              </div>
            </div>

            <p className="fallback-tip">
              Didn't get the email? Check your spam or promotions folder.
              {orderInfo?.intake_url && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="fallback-link"
                    onClick={handleContinue}
                    data-testid="fallback-open-intake"
                  >
                    Or open your intake form here →
                  </button>
                </>
              )}
            </p>

            <button
              type="button"
              className="home-btn"
              onClick={() => navigate('/')}
              data-testid="go-home-btn"
            >
              Back to homepage
            </button>
          </>
        )}

        {/* Failed */}
        {status === 'failed' && (
          <>
            <div className="icon-circle failed">
              <XCircle />
            </div>
            <h1 className="title">Payment Issue</h1>
            <p className="subtitle">
              We couldn't confirm your payment. This might be due to a cancelled transaction or a temporary issue.
            </p>
            <button 
              className="retry-btn"
              onClick={() => navigate('/checkout', { replace: true })}
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
